'use client'

import Link from 'next/link'
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { CHOICES_PANEL, GPC_LINE } from '@/components/privacy/consent-copy'

/**
 * The rows of Your privacy choices (Counsel G3 2026-10-22 section 2). Shared by
 * the banner's dialog and the /privacy-choices page; the caller supplies the
 * title (a DialogTitle in the dialog, the page heading on the page).
 *
 * GPC: the Ads switch stays off and disabled. The Analytics switch stays
 * usable so a visitor can affirmatively turn analytics on (G3); marketing
 * never turns on while the browser sends GPC.
 */
export function PrivacyChoicesPanel({
  analytics,
  marketing,
  gpc,
  onAnalyticsChange,
  onMarketingChange,
  onSave,
  layout = 'dialog',
}: {
  analytics: boolean
  marketing: boolean
  gpc: boolean
  onAnalyticsChange: (next: boolean) => void
  onMarketingChange: (next: boolean) => void
  onSave: () => void
  /** 'page' keeps each control beside its title from sm up instead of pushing it to the far edge of a wide row. */
  layout?: 'dialog' | 'page'
}) {
  const rowClass =
    layout === 'page'
      ? 'flex items-start justify-between gap-3 sm:justify-start sm:gap-4'
      : 'flex items-start justify-between gap-3'
  return (
    <div className="grid gap-4" data-privacy-choices="panel">
      {gpc ? (
        <p className="text-sm text-foreground" data-consent-gpc="line">
          {GPC_LINE}
        </p>
      ) : null}
      <div className="grid gap-1" data-consent-essential="always">
        <div className={rowClass}>
          <span className="min-w-0 text-sm font-medium text-foreground">{CHOICES_PANEL.neededTitle}</span>
          <span className="shrink-0 text-sm text-muted-foreground">{CHOICES_PANEL.neededState}</span>
        </div>
        <p className="text-sm text-muted-foreground">{CHOICES_PANEL.neededBody}</p>
      </div>
      <div className="grid gap-1">
        <Label className={rowClass}>
          <span className="min-w-0 text-sm font-medium">{CHOICES_PANEL.analyticsTitle}</span>
          <Switch
            // 44px hit area on an 18px track (site audit 2026-10-21, 2.10).
            className="mt-0.5 shrink-0 after:-inset-y-3.5"
            data-consent-toggle="analytics"
            checked={analytics}
            onCheckedChange={(checked) => onAnalyticsChange(checked === true)}
          />
        </Label>
        <p className="text-sm text-muted-foreground">{CHOICES_PANEL.analyticsBody}</p>
      </div>
      <div className="grid gap-1">
        <Label className={rowClass}>
          <span className="min-w-0 text-sm font-medium">{CHOICES_PANEL.adsTitle}</span>
          <Switch
            // 44px hit area on an 18px track (site audit 2026-10-21, 2.10).
            className="mt-0.5 shrink-0 after:-inset-y-3.5"
            data-consent-toggle="marketing"
            checked={gpc ? false : marketing}
            disabled={gpc}
            onCheckedChange={(checked) => onMarketingChange(checked === true)}
          />
        </Label>
        <p className="text-sm text-muted-foreground">{CHOICES_PANEL.adsBody}</p>
      </div>
      <Button type="button" className="h-11 w-full" data-consent-action="save" onClick={onSave}>
        {CHOICES_PANEL.save}
      </Button>
      <p className="text-sm text-muted-foreground">
        {CHOICES_PANEL.readMoreBefore}
        <Link href="/privacy#choices" className="font-medium text-foreground underline hover:no-underline">
          {CHOICES_PANEL.readMoreLink}
        </Link>
        {CHOICES_PANEL.readMoreAfter}
      </p>
    </div>
  )
}
